import { Alert } from '@creditornot/cb-alert';
import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { getSelfDeliveryAreas, updateSelfDeliveryAreas } from 'apis/delivery-settings-api/api';
import { DeliveryAreaPatchData } from 'apis/delivery-settings-api/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { typographyCss } from 'common/typography';

import DeliveryAreaMap, { MapData } from './DeliveryAreaMap';
import DeliveryAreasForm from './DeliveryAreasForm';
import DeliveryAreasView from './DeliveryAreasView';
import {
  sendTelemetrySaveDeliveryAreas,
  sendTelemetryToggleDeliveryArea,
  useTelemetryDeliveryAreasImpression,
} from './telemetry';
import { convertDeliveryAreas } from './utils';

type DeliveryAreasProps = {
  className?: string;
  venueId: string;
  venueLocation?: Venue['location'];
  currency: Currency;
  hasEditPermission: boolean;
  showHybridDeliveryInfo?: boolean;
};

const Root = styled.div`
  margin-block-end: 32px;
  padding-block-end: 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Header = styled.h3`
  ${typographyCss.Heading6}
  margin-block-end: 16px;
`;

const StyledAlert = styled(Alert)`
  margin-block-end: 16px;
`;

const HelpIcon = styled(InfoCircleFilled)`
  fill: ${cssThemedValue('textSecondary')};
  width: 12px;
  margin-inline-start: 8px;
`;

const TooltipContent = styled.div`
  ${typographyCss.Body3}
  width: 313px;
  max-width: 100%;
  padding: 16px;
`;

const DeliveryAreas = ({
  className,
  venueId,
  venueLocation,
  currency,
  hasEditPermission,
  showHybridDeliveryInfo,
}: DeliveryAreasProps) => {
  useTelemetryDeliveryAreasImpression();

  const theme = useTheme();
  const [editing, setEditing] = useState(false);

  const {
    data: deliveryAreasData,
    refetch: refetchDeliveryAreas,
    error,
    isLoading,
  } = useQuery([`delivery-settings/${venueId}/self-delivery-areas`], () => getSelfDeliveryAreas(venueId), {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const deliveryAreas = useMemo(
    () => convertDeliveryAreas(deliveryAreasData, venueLocation?.coordinates),
    [deliveryAreasData, venueLocation],
  );

  const { mutate, isLoading: isSubmitting } = useMutation(
    (deliveryAreas: DeliveryAreaPatchData[]) => updateSelfDeliveryAreas(venueId, deliveryAreas),
    {
      onSuccess: () => {
        toast.success(<FormattedMessage id="venue-details.delivery-areas.success" />);
        refetchDeliveryAreas();
        setEditing(false);
      },
      onError: error => {
        if (isAxiosError(error) && (error.response?.data?.message || error.message)) {
          toast.error(error.response?.data?.message || error.message, { duration: Infinity });
        }
      },
    },
  );

  const [mapData, setMapData] = useState<MapData>([]);
  useEffect(() => {
    setMapData(deliveryAreas);
  }, [deliveryAreas]);

  if (isLoading || error || !deliveryAreas.length) {
    return null;
  }

  const canToggle = deliveryAreas.length > 1;
  const editable = hasEditPermission && canToggle;

  const handleSubmit = async ({ deliveryAreas }: { deliveryAreas: DeliveryAreaPatchData[] }) => {
    if (!editable) {
      return;
    }
    // Make sure only the wanted attributes are included in the payload
    const patchData = deliveryAreas.map(({ id, disabled }) => ({ id, disabled }));
    mutate(patchData);
    sendTelemetrySaveDeliveryAreas();
  };

  return (
    <Root className={className}>
      <Header>
        <FormattedMessage id="venue-details.delivery-areas.title" />
        <Popover
          disableContentPadding
          placement={theme.cbDirection === 'ltr' ? 'right' : 'left'}
          fallbackPlacements={['auto']}
          content={
            <TooltipContent>
              {!editable ? (
                <FormattedMessage id="venue-details.delivery-areas.help-text-no-edit" />
              ) : (
                <FormattedMessage id="venue-details.delivery-areas.help-text" />
              )}
            </TooltipContent>
          }
        >
          <HelpIcon />
        </Popover>
      </Header>

      {showHybridDeliveryInfo && (
        <StyledAlert variant="info">
          <FormattedMessage id="venue-details.delivery-areas.hybrid-delivery-info-banner" />
        </StyledAlert>
      )}

      <DeliveryAreaMap deliveryAreas={mapData} venueLocation={venueLocation} />

      {editing ? (
        <DeliveryAreasForm
          deliveryAreas={deliveryAreas}
          currency={currency}
          isSubmitting={isSubmitting}
          onUpdate={(id, disabled) => {
            setMapData(mapData.map(area => ({ ...area, disabled: area.id === id ? disabled : area.disabled })));
            sendTelemetryToggleDeliveryArea();
          }}
          onSubmit={handleSubmit}
          onCancel={() => {
            setEditing(false);
            setMapData(deliveryAreas);
          }}
        />
      ) : (
        <DeliveryAreasView
          deliveryAreas={deliveryAreas}
          currency={currency}
          hideToggles={!canToggle}
          onEditClick={editable ? () => setEditing(true) : undefined}
        />
      )}
    </Root>
  );
};

export default DeliveryAreas;
